/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — pure participant-list helpers for the booking form
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      -
 *   siblings:  booking-calendar.js, booking-steps.js, booking-filters.js, booking-request.js, booking-validation.js
 *   tests:     booking-validation-feedback-test.js (peopleForConfirm)
 *
 * Pure participant-list helpers carved out of controllers/index.js. No `this`:
 * every input is an explicit parameter. rebuildVoucherPeople resizes the
 * people list to a voucher's seat count without dropping rows the user already
 * filled; meUiFlags mirrors which contact fields are already owned by the
 * booking contact so the form can lock them; peopleForConfirm is the people
 * list the confirm POST sends (a voucher booking adds the contact itself).
 */
export function blankPerson() {
  return {
    first_name: null,
    id: null,
    last_name: null,
    mobile: null,
    data_nascita: null,
  };
}

// Builds a participant row. Passing the booking contact pre-fills and locks the
// fields it already owns; passing null yields an empty row.
export function buildPerson(me) {
  return {
    first_name: me?.first_name || null,
    id: me?.id || null,
    same: !!me,
    lock_first_name: !!me && !!me.first_name,
    lock_last_name: !!me && !!me.last_name,
    last_name: me?.last_name || null,
    mobile: me?.mobile || null,
    data_nascita: me?.data_nascita || null,
    luogo_nascita: me?.luogo_nascita || null,
    cod_fiscale: me?.cod_fiscale || null,
  };
}

// Rebuilds the participant list for a voucher of `peopleNumber` seats instead of
// appending on every slot click: keeps rows the user already filled (and a
// single "me" row), drops empty placeholders and duplicate "me" rows, then pads
// with blank rows up to the voucher size. The "me" row (when present) counts as
// one of the covered seats; otherwise the booking contact is implicit.
export function rebuildVoucherPeople(people, peopleNumber) {
  const isBlank = (p) =>
    !p.id &&
    !p.first_name &&
    !p.last_name &&
    !p.mobile &&
    !p.data_nascita &&
    !p.luogo_nascita &&
    !p.cod_fiscale;

  let meSeen = false;
  const kept = (Array.isArray(people) ? people : []).filter((p) => {
    if (p.same) {
      if (meSeen) {
        return false;
      }
      meSeen = true;
      return true;
    }
    return !isBlank(p);
  });

  const target = meSeen ? peopleNumber : peopleNumber - 1;
  const newPeople = [...kept];
  while (newPeople.length < target) {
    newPeople.push({
      first_name: null,
      last_name: null,
      id: null,
      same: false,
      mobile: null,
      data_nascita: null,
    });
  }

  return newPeople;
}

// UI-only flags mirrored onto the contact record so the form can disable the
// fields the account already owns.
export function meUiFlags(me) {
  const {
    first_name: firstName,
    last_name: lastName,
    ragione_sociale: ragioneSociale,
    fisica_giuridica: fisicaGiuridica,
  } = me;

  const hasFirstName = !!firstName;
  const hasLastName = !!lastName;
  const hasRagioneSociale = !!ragioneSociale;
  const hasFisicaGiuridica = fisicaGiuridica === true || fisicaGiuridica === false;

  return {
    warning_new_reg: hasFirstName || hasLastName || hasRagioneSociale,
    disable_fisica_giuridica: hasFisicaGiuridica,
    disable_first_name: hasFirstName,
    disable_last_name: hasLastName,
    disable_ragione_sociale: hasRagioneSociale,
  };
}

// The participants sent with the confirm POST: a copy of the list, plus the
// booking contact itself (`same: true`) when a voucher is being used.
export function peopleForConfirm(people, me, voucherToUse) {
  const list = Array.isArray(people) ? [...people] : [];
  if (voucherToUse) {
    list.push({
      id: me.id,
      first_name: me.first_name,
      last_name: me.last_name,
      mobile: me.mobile,
      data_nascita: me.data_nascita,
      same: true,
    });
  }
  return list;
}
